import { AxiosHeaders, type AxiosResponse } from 'axios';
import { useCallback } from 'react';

import { corePayeeInvoicesClient } from 'app/api/apiClient/generated/corePayeeInvoicesClient';
import { corePayeePaymentsClient } from 'app/api/apiClient/generated/corePayeePaymentsClient';
import { settlementBillsClient } from 'app/api/apiClient/settlement/settlementBillsClient';
import { useCanViewPaperwork } from 'app/api/hooks/paperwork/v1/bills';
import { billParams } from 'app/constants/param-names';
import { queries } from 'app/constants/queries';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';
import { defaultPayeeInvoicesQueryParams } from 'app/pages/payee/Invoices/helpers';
import { defaultPayeePaperworkQueryParams } from 'app/pages/payee/Paperwork/helpers';
import { defaultPayeePaymentsQueryParams } from 'app/pages/payee/Payments/helpers';

import { type PayeeGlobalSearchResult } from './helpers';

const pageSize = 3;
const page = 0;

export const usePayeeGlobalSearch = (searchString: string) => {
  const canViewPaperwork = useCanViewPaperwork();

  const queryFn = useCallback(async (): Promise<AxiosResponse<PayeeGlobalSearchResult>> => {
    const paymentsQuery = corePayeePaymentsClient.searchPayments({
      ...defaultPayeePaymentsQueryParams,
      page,
      pageSize,
      payeeRelationshipId: undefined, // working around TS issue
      q: searchString,
    });
    const invoicesQuery = corePayeeInvoicesClient.searchInvoicesAndCreditNotes({
      ...defaultPayeeInvoicesQueryParams,
      page,
      pageSize,
      q: searchString,
    });
    const paperworkQuery = canViewPaperwork
      ? settlementBillsClient.getBills({
        ...defaultPayeePaperworkQueryParams,
        [billParams.search]: searchString,
        page,
        pageSize,
      })
      : null;

    const [payments, invoices, paperwork] = await Promise.all([
      paymentsQuery,
      invoicesQuery,
      paperworkQuery ?? Promise.resolve(null),
    ]);

    return {
      config: {
        headers: new AxiosHeaders(),
      },
      data: {
        bills: paperwork?.data.bills ?? [],
        invoices: invoices?.data.invoices ?? [],
        payments: payments?.data.payments ?? [],
      },
      headers: new AxiosHeaders(),
      status: 200,
      statusText: 'OK',
    };
  }, [canViewPaperwork, searchString]);

  return useQueryEnhanced(
    [queries.payeeGlobalSearch, searchString],
    queryFn,
    {
      enabled: !!searchString,
    },
  );
};
